import { useState } from 'react';
import { Size, itemLookUpPath, t, type LookupStartAnswer } from '@bms2/shared';
import { Button, TextInput } from '../../components/controls';
import { FormFields, FormFrame } from '../../components/forms';
import { post } from './api';
import { ExportLookupRequestButton } from './ItemLookupActs';
import { LookupStepThrough } from './LookupStepThrough';

/**
 * 🔒 Look Up Manufacturer on the Item page and the Item's edit window (piece 16,
 * *Manufacturer lookup*; decision 12660; T3814): the manufacturer and part
 * number, or a pasted product page address, sent to the lookup seam for this
 * Item alone. A proposal opens the same Step Through a bundle opens; a driver
 * that cannot read the page says so in a sentence and offers Export Lookup
 * Request for this Item. Nothing is written until a step accepts it.
 */
export function LookUpManufacturerButton({ itemId, manufacturer, partNumber, apiBaseUrl = '', onWrote, disabledReason = null }: {
  itemId: string;
  manufacturer: string | null;
  partNumber: string | null;
  apiBaseUrl?: string;
  onWrote: () => void;
  /** Where the press waits on something else, the sentence saying what; the button then stands disabled. */
  disabledReason?: string | null;
}) {
  const [asking, setAsking] = useState(false);
  const [values, setValues] = useState({ manufacturer: manufacturer ?? '', partNumber: partNumber ?? '', address: '' });
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const shut = () => {
    setAsking(false);
    setRefusal(null);
    setSaved(null);
  };
  const lookUp = async () => {
    setBusy(true);
    setRefusal(null);
    const answer = await post<LookupStartAnswer>(itemLookUpPath(itemId), values, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setRefusal(answer.message);
    if (!answer.data.ok) return setRefusal(answer.data.refusal);
    shut();
    setRunId(answer.data.runId);
  };
  return (
    <>
      <span data-record-action="look-up-manufacturer">
        <Button size={Size.Small} text={[t('itemLookup.lookUp.action')]} actions={[() => setAsking(true)]} disabled={disabledReason !== null} {...(disabledReason ? { disabledReason } : {})} />
      </span>
      {/* Mounted only while open, so a second look up starts from the Item's own manufacturer and part number. */}
      {asking ? (
        <FormFrame
          host="window"
          open
          title={t('itemLookup.lookUp.title')}
          onCancel={shut}
          dataForm="look-up-manufacturer"
          typed={values.address.trim() !== ''}
          refusal={refusal}
          onDismissRefusal={() => setRefusal(null)}
          act={{ label: t('itemLookup.lookUp.go'), onAct: () => void lookUp(), disabled: busy }}
        >
          <FormFields>
            <p>{t('itemLookup.lookUp.hint')}</p>
            <TextInput size={Size.Medium} label={t('itemLookup.lookUp.manufacturer')} value={values.manufacturer} onChange={(manufacturerText) => setValues((held) => ({ ...held, manufacturer: manufacturerText }))} name="lookupManufacturer" />
            <TextInput size={Size.Medium} label={t('itemLookup.lookUp.partNumber')} value={values.partNumber} onChange={(part) => setValues((held) => ({ ...held, partNumber: part }))} name="lookupPartNumber" />
            <TextInput size={Size.Large} label={t('itemLookup.lookUp.address')} value={values.address} onChange={(address) => setValues((held) => ({ ...held, address }))} name="lookupAddress" type="url" />
          </FormFields>
          {/* 🔒 A driver that cannot read the page offers Export Lookup Request for this Item, the road around the network (decision 12660). */}
          {refusal ? (
            <div data-look-up-refused="">
              <ExportLookupRequestButton selectedCount={1} resolve={async () => ({ ok: true, ids: [itemId] })} apiBaseUrl={apiBaseUrl} onSaved={setSaved} onRefused={setRefusal} />
              {saved ? <p>{saved}</p> : null}
            </div>
          ) : null}
        </FormFrame>
      ) : null}
      {runId ? <LookupStepThrough key={runId} runId={runId} apiBaseUrl={apiBaseUrl} onClosed={(wrote) => { setRunId(null); if (wrote) onWrote(); }} /> : null}
    </>
  );
}
